import React, { useState } from 'react';
import { Button, Icon, Input, FormAlert, Text, RadioGroup } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import type { AccountType } from '../types';
import {
  getNameFieldError,
  getEmailFieldError,
  getOrgNameFieldError,
} from '../../utils/userFieldValidation';
import { validatePassword } from '../../utils/passwordValidation';
import { generatePassword } from '../../utils/passwordGenerator';
import adminApi, { handleAdminApiError } from '../services/adminApi';

interface AdminCreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface CreateUserForm {
  firstName: string;
  lastName: string;
  email: string;
  organizationName: string;
  password: string;
  role: AccountType;
}

const INITIAL_FORM: CreateUserForm = {
  firstName: '',
  lastName: '',
  email: '',
  organizationName: '',
  password: '',
  role: 'user',
};

const ACCOUNT_TYPE_OPTIONS = [
  { value: 'user', label: 'Standard user', description: 'Owns and creates projects' },
  { value: 'guest', label: 'Guest', description: "Can only view projects they're invited to" },
];

export const AdminCreateUserModal: React.FC<AdminCreateUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [form, setForm] = useState<CreateUserForm>(INITIAL_FORM);
  const [isClosing, setIsClosing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  // The password is the only copy anyone has, so once the account exists the panel
  // stays open and locked until the admin closes it themselves.
  const [isCreated, setIsCreated] = useState(false);
  const [copied, setCopied] = useState(false);

  // Field touched states for validation
  const [firstNameTouched, setFirstNameTouched] = useState(false);
  const [lastNameTouched, setLastNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [organizationNameTouched, setOrganizationNameTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);

  const resetForm = () => {
    setForm(INITIAL_FORM);
    setIsLoading(false);
    setError('');
    setSuccessMessage('');
    setIsCreated(false);
    setCopied(false);
    setFirstNameTouched(false);
    setLastNameTouched(false);
    setEmailTouched(false);
    setOrganizationNameTouched(false);
    setPasswordTouched(false);
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      resetForm();
      onClose();
    }, 300);
  };

  // Validation helpers — rules are shared with SignupPage via userFieldValidation
  const getFirstNameError = (): string =>
    firstNameTouched ? getNameFieldError(form.firstName, 'First name') : '';

  const getLastNameError = (): string =>
    lastNameTouched ? getNameFieldError(form.lastName, 'Last name') : '';

  const getEmailError = (): string =>
    emailTouched ? getEmailFieldError(form.email) : '';

  const getOrganizationNameError = (): string =>
    organizationNameTouched ? getOrgNameFieldError(form.organizationName) : '';

  const getPasswordFieldError = (value: string): string => {
    if (!value) return 'Password is required';
    const validation = validatePassword(value);
    return validation.isValid ? '' : validation.errors[0];
  };

  const getPasswordError = (): string =>
    passwordTouched ? getPasswordFieldError(form.password) : '';

  const hasFormErrors = (): boolean =>
    !!getNameFieldError(form.firstName, 'First name') ||
    !!getNameFieldError(form.lastName, 'Last name') ||
    !!getEmailFieldError(form.email) ||
    !!getOrgNameFieldError(form.organizationName) ||
    !!getPasswordFieldError(form.password);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError('');
    if (name === 'password') {
      setPasswordTouched(true);
      setCopied(false);
    }
  };

  const handleGeneratePassword = () => {
    setForm((prev) => ({ ...prev, password: generatePassword() }));
    setPasswordTouched(true);
    setCopied(false);
    setError('');
  };

  const handleCopyPassword = async () => {
    if (!form.password) return;
    try {
      await navigator.clipboard.writeText(form.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Clipboard access can be blocked (insecure origin, denied permission).
      // The password is on screen in plain text either way, so this is cosmetic.
      console.error('Failed to copy password:', err);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setError('');

    // Mark all fields as touched
    setFirstNameTouched(true);
    setLastNameTouched(true);
    setEmailTouched(true);
    setOrganizationNameTouched(true);
    setPasswordTouched(true);

    // Client-side validation — same shared rules as the inline field errors
    const submitError =
      getNameFieldError(form.firstName, 'First name') ||
      getNameFieldError(form.lastName, 'Last name') ||
      getEmailFieldError(form.email) ||
      getOrgNameFieldError(form.organizationName) ||
      getPasswordFieldError(form.password);
    if (submitError) {
      setError(submitError);
      setIsLoading(false);
      return;
    }

    try {
      const result = await adminApi.createUser({
        email: form.email,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        organizationName: form.organizationName.trim() || undefined,
        password: form.password,
        role: form.role,
      });

      // Stay open. The password is only in this panel — closing automatically
      // would take it away before the admin has passed it on.
      setIsCreated(true);
      setSuccessMessage(result.message);
      setIsLoading(false);
      onSuccess();
    } catch (err: any) {
      setError(handleAdminApiError(err));
      setIsLoading(false);
    }
  };

  if (!isOpen && !isClosing) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-alpha-black-70 z-[200] ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
        onClick={handleClose}
      />
      {/* Panel */}
      <div
        className={`fixed right-0 top-0 h-screen z-[200] bg-bg-primary w-[480px] flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        style={{
          boxShadow: '0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)',
        }}
      >
        {/* Header */}
        <div className="flex p-bg-v2-3xl items-center gap-bg-v2-md self-stretch justify-between">
          <Text variant="text-xl" weight="bold">Add User</Text>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200"
          >
            <Icon name={IconName.Cross} size="lg" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        {/* Content */}
        <div className="flex flex-col gap-bg-v2-2xl self-stretch px-bg-v2-3xl py-bg-v2-3xl overflow-y-auto flex-1">
          {/* First Name and Last Name */}
          <div className="grid grid-cols-2 gap-bg-v2-lg">
            <Input
              id="admin-create-firstName"
              disabled={isCreated}
              name="firstName"
              type="text"
              label="First Name"
              placeholder="Enter first name"
              required
              errorText={getFirstNameError()}
              value={form.firstName}
              onChange={handleInputChange}
              onBlur={() => setFirstNameTouched(true)}
            />
            <Input
              id="admin-create-lastName"
              disabled={isCreated}
              name="lastName"
              type="text"
              label="Last Name"
              placeholder="Enter last name"
              required
              errorText={getLastNameError()}
              value={form.lastName}
              onChange={handleInputChange}
              onBlur={() => setLastNameTouched(true)}
            />
          </div>

          {/* Email */}
          <Input
            id="admin-create-email"
            disabled={isCreated}
            name="email"
            type="email"
            label="Email"
            placeholder="Enter email address"
            required
            errorText={getEmailError()}
            startIcon={<Icon name={IconName.Mail} size="md" />}
            value={form.email}
            onChange={handleInputChange}
            onBlur={() => setEmailTouched(true)}
          />

          {/* Organization */}
          <Input
            id="admin-create-organizationName"
            disabled={isCreated}
            name="organizationName"
            type="text"
            label="Company/Organization (Optional)"
            placeholder="Enter company/organization name"
            errorText={getOrganizationNameError()}
            startIcon={<Icon name={IconName.Organization} size="md" />}
            value={form.organizationName}
            onChange={handleInputChange}
            onBlur={() => setOrganizationNameTouched(true)}
          />

          <RadioGroup
            name="admin-create-role"
            label="Account Type"
            options={ACCOUNT_TYPE_OPTIONS}
            value={form.role}
            onChange={(value) => setForm((prev) => ({ ...prev, role: value as AccountType }))}
            disabled={isCreated}
          />

          {/* Password — deliberately shown in plain text: the admin has to read it off
              the screen to pass it on, so masking it defeats the purpose.
              Generate/Copy live in the label row rather than under the field: the
              strength meter and requirements list grow and shrink as the admin types,
              and buttons below them would move around under the cursor. */}
          <div className="flex flex-col gap-bg-v2-sm">
            <div className="flex items-center justify-between gap-bg-v2-md">
              <label
                htmlFor="admin-create-password"
                className="font-bg-v2-sans font-bg-v2-medium text-bg-v2-sm text-text-secondary"
              >
                Password
              </label>
              <Button
                variant="link"
                size="very-small"
                label="Generate"
                onClick={handleGeneratePassword}
                disabled={isLoading || isCreated}
              />
            </div>
            <Input
              id="admin-create-password"
              disabled={isCreated}
              name="password"
              type="text"
              placeholder="Enter or generate a password"
              required
              errorText={getPasswordError()}
              startIcon={<Icon name={IconName.Lock} size="md" />}
              endIcon={<Icon name={copied ? IconName.Check : IconName.Copy} size="md" />}
              onEndIconClick={handleCopyPassword}
              value={form.password}
              onChange={handleInputChange}
              onBlur={() => setPasswordTouched(true)}
              showStrength={true}
            />
          </div>

          {error && <FormAlert variant="error" message={error} />}
          {successMessage && <FormAlert variant="success" message={successMessage} />}
        </div>

        {/* Footer */}
        <div className="flex p-bg-v2-xl px-bg-v2-3xl justify-end items-center gap-bg-v2-xl self-stretch border-t border-border-secondary bg-bg-primary">
          <Button
            variant="outline"
            label={isCreated ? 'Close' : 'Cancel'}
            onClick={handleClose}
            disabled={isLoading}
          />
          <Button
            variant="primary"
            label={isLoading ? 'Creating...' : 'Create User'}
            onClick={handleSubmit}
            disabled={isLoading || isCreated || hasFormErrors()}
            loading={isLoading}
          />
        </div>
      </div>
    </>
  );
};

export default AdminCreateUserModal;
